import { chartAppearance, isChartInstrument } from 'lib-analytics-ui';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { useMap, useUpdateEffect } from 'react-use';
import { getNiceTickValues } from 'recharts-scale';

import {
  InstrumentComparisonSyncOptions,
  InstrumentComparisonSyncOptionsUpdateFn,
} from './instrumentComparison.types';
import { calculateChartSeriesMaxValue } from './utils/calculateChartSeriesMaxValue';
import { useComparisonInstrumentsQueryState } from './utils/useComparisonInstrumentsQueryState';

type InstrumentComparisonProviderProps = PropsWithChildren;

/**
 * The value returned by the context. If undefined, it means the instruments are
 * not rendered in comparison mode and thus no sync options are available.
 */
export type InstrumentComparisonContextValue = {
  readonly syncOptions: InstrumentComparisonSyncOptions;
  readonly setSyncOptions: InstrumentComparisonSyncOptionsUpdateFn;
};

const InstrumentComparisonContext = createContext<
  InstrumentComparisonContextValue | undefined
>(undefined);

export function InstrumentComparisonProvider({
  children,
}: InstrumentComparisonProviderProps): JSX.Element {
  const { left, right } = useComparisonInstrumentsQueryState();
  const [syncOptions, { set }] = useMap<InstrumentComparisonSyncOptions>({});

  const setSyncOptions: InstrumentComparisonSyncOptionsUpdateFn = useCallback(
    (type, value) => {
      set(type, { ...syncOptions[type], ...value });
    },
    [set, syncOptions],
  );

  useUpdateEffect(() => {
    if (!left.isSuccess || !right.isSuccess) return;

    const charts = [
      ...left.instruments.filter(isChartInstrument),
      ...right.instruments.filter(isChartInstrument),
    ];

    if (charts.length) {
      const maxValue = calculateChartSeriesMaxValue(charts);
      const ticks = getNiceTickValues(
        [0, maxValue],
        chartAppearance.tickCount,
        false,
      );

      setSyncOptions('chart', {
        tooltip: true,
        ticks,
      });
    }
  }, [left, right]);

  const contextValue: InstrumentComparisonContextValue = useMemo(
    () => ({ syncOptions, setSyncOptions }),
    [syncOptions, setSyncOptions],
  );

  return (
    <InstrumentComparisonContext.Provider value={contextValue}>
      {children}
    </InstrumentComparisonContext.Provider>
  );
}

export function useInstrumentComparison():
  | InstrumentComparisonContextValue
  | undefined {
  return useContext(InstrumentComparisonContext);
}
